export interface Vector2 {
  x: number
  y: number
}

export function distance(a: Vector2, b: Vector2): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  return Math.sqrt(dx * dx + dy * dy)
}

export function distanceSquared(a: Vector2, b: Vector2): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  return dx * dx + dy * dy
}

export function normalize(v: Vector2): Vector2 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y)
  if (len === 0) return { x: 0, y: 0 }
  return { x: v.x / len, y: v.y / len }
}

export function add(a: Vector2, b: Vector2): Vector2 {
  return { x: a.x + b.x, y: a.y + b.y }
}

export function subtract(a: Vector2, b: Vector2): Vector2 {
  return { x: a.x - b.x, y: a.y - b.y }
}

export function scale(v: Vector2, s: number): Vector2 {
  return { x: v.x * s, y: v.y * s }
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

export function lerpVec(a: Vector2, b: Vector2, t: number): Vector2 {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t)
  }
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

export function randomRange(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

export function randomInt(min: number, max: number): number {
  return Math.floor(randomRange(min, max + 1))
}

// Simple seeded random for reproducible map generation
export class SeededRandom {
  private seed: number

  constructor(seed: number) {
    this.seed = seed
  }

  next(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff
    return this.seed / 0x7fffffff
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min)
  }

  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1))
  }
}

// Simple 2D noise for terrain generation
export function noise2D(x: number, y: number, seed: number = 0): number {
  const hash = (n: number) => {
    n = ((n >> 13) ^ n)
    n = (n * (n * n * 15731 + 789221) + 1376312589) & 0x7fffffff
    return n / 0x7fffffff
  }

  const ix = Math.floor(x)
  const iy = Math.floor(y)
  const fx = x - ix
  const fy = y - iy

  const n00 = hash(ix + iy * 57 + seed * 131)
  const n10 = hash((ix + 1) + iy * 57 + seed * 131)
  const n01 = hash(ix + (iy + 1) * 57 + seed * 131)
  const n11 = hash((ix + 1) + (iy + 1) * 57 + seed * 131)

  const sx = fx * fx * (3 - 2 * fx)
  const sy = fy * fy * (3 - 2 * fy)

  const n0 = lerp(n00, n10, sx)
  const n1 = lerp(n01, n11, sx)

  return lerp(n0, n1, sy)
}

// Octave noise for more natural terrain
export function octaveNoise(x: number, y: number, octaves: number, persistence: number, seed: number = 0): number {
  let total = 0
  let frequency = 1
  let amplitude = 1
  let maxValue = 0

  for (let i = 0; i < octaves; i++) {
    total += noise2D(x * frequency, y * frequency, seed + i) * amplitude
    maxValue += amplitude
    amplitude *= persistence
    frequency *= 2
  }

  return total / maxValue
}
